import React, { useEffect, useMemo, useState } from 'react';
import {
  FaArrowLeft,
  FaCheck,
  FaCheckCircle,
  FaChevronRight,
  FaDrawPolygon,
  FaExclamationTriangle,
  FaHistory,
  FaLayerGroup,
  FaLock,
  FaMapMarkedAlt,
  FaSeedling,
  FaShieldAlt,
} from 'react-icons/fa';
import MigrationPerimeterEditor from './MigrationPerimeterEditor';
import { plotMigrationRuntime } from '../config/plotMigrationAccess';
import {
  effectuateOwnedPlotMigration,
  loadInternalPlotMigrationReview,
  loadOwnedPlotMigrationReview,
} from '../services/plotMigrationReviewService';
import './MigrationReviewLocal.css';
import './PlotMigrationWizard.css';

const LOCAL_REVIEW_DATA_URL = '/local-migration-review/review-data.json';
const CLIENT_PREVIEW_DATA = Object.freeze({
  somenteProprietario: true,
  estruturaLegadaAlterada: false,
  ativacaoAutomatica: false,
  propriedades: [{
    id: 'preview-property-rio-claro',
    nome: 'Fazenda Rio Claro · exemplo',
    coordenadaX: -51.529,
    coordenadaY: -20.827,
    statusMigracao: 'em_revisao',
    geometria: {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [-51.5361, -20.8197], [-51.519, -20.82], [-51.5175, -20.831],
          [-51.5297, -20.836], [-51.5365, -20.8322], [-51.5361, -20.8197],
        ]],
      },
    },
    talhoes: [
      {
        id: 'preview-physical-pivot',
        nome: 'Pivô Central',
        area: 24,
        estadoMigracao: 'em_revisao',
        mapeamentoStatus: 'preparado',
        estrategia: 'um_para_um_seguro',
        geometria: {
          type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [[
            [-51.5328, -20.8212], [-51.5264, -20.821], [-51.5248, -20.8258],
            [-51.5292, -20.828], [-51.5335, -20.8256], [-51.5328, -20.8212],
          ]] },
        },
        legado: { id: 'preview-legacy-pivot-2025', nome: 'Soja 2024/2025', cultura: 'Soja', area: 24, dataPlantio: '2024-10-18', dataColheita: '2025-02-26' },
        plantios: [
          { id: 'preview-planting-soy', cultura: 'Soja', variedade: 'Exemplo 80I82', dataPlantio: '2024-10-18', dataColheita: '2025-02-26', status: 'colhido', area: 24 },
          { id: 'preview-planting-corn', cultura: 'Milho', variedade: 'Exemplo 30F53', dataPlantio: '2025-03-12', dataColheita: '2025-07-28', status: 'colhido', area: 24 },
        ],
      },
      {
        id: 'preview-physical-north',
        nome: 'Talhão Norte',
        area: 18.5,
        estadoMigracao: 'em_revisao',
        mapeamentoStatus: 'pendente_revisao',
        estrategia: 'um_para_um_seguro',
        geometria: null,
        legado: { id: 'preview-legacy-north-2025', nome: 'Milho Norte 2025', cultura: 'Milho', area: 18.5, dataPlantio: '2025-02-08', dataColheita: '2025-06-22' },
        plantios: [
          { id: 'preview-planting-north', cultura: 'Milho', variedade: 'Exemplo AG 8700', dataPlantio: '2025-02-08', dataColheita: '2025-06-22', status: 'colhido', area: 18.5 },
        ],
      },
      {
        id: 'preview-physical-pasture',
        nome: 'Pastagem Sul',
        area: 15.2,
        estadoMigracao: 'em_revisao',
        mapeamentoStatus: 'preparado',
        estrategia: 'um_para_um_seguro',
        geometria: {
          type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [[
            [-51.5324, -20.8284], [-51.5266, -20.8286], [-51.5258, -20.8323],
            [-51.5301, -20.8342], [-51.5336, -20.8318], [-51.5324, -20.8284],
          ]] },
        },
        legado: { id: 'preview-legacy-pasture', nome: 'Braquiária 2023', cultura: 'Braquiária', area: 15.2, dataPlantio: '2023-11-06', dataColheita: null },
        plantios: [
          { id: 'preview-planting-pasture', cultura: 'Braquiária', dataPlantio: '2023-11-06', status: 'ativo', area: 15.2 },
        ],
      },
    ],
  }],
});

const formatArea = value => {
  const area = Number(value);
  return Number.isFinite(area) && area > 0
    ? `${area.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ha`
    : 'Área não informada';
};

const formatDate = value => {
  if (!value) return 'Não informada';
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('pt-BR').format(date);
};

const draftKey = plot => String(plot?.legado?.id || plot?.id || '');

function initialDraft(plot) {
  const review = plot?.revisao;
  return {
    decision: review?.decisao || 'manter_separado',
    plotName: review?.nomeTalhaoProposto || plot?.nome || '',
    destinationPhysicalPlotId: review?.talhaoFisicoDestinoId || '',
    geometry: review?.geometriaProposta || null,
    calculatedArea: review?.areaCalculada || null,
    justification: review?.justificativa || '',
    saved: review?.status === 'efetivado',
    status: review?.status || 'pendente',
  };
}

function PlantingHistory({ plantings = [] }) {
  if (!plantings.length) {
    return <p className="plot-migration-empty-history">Nenhum plantio foi identificado para esta área.</p>;
  }

  return (
    <div className="plot-migration-history">
      {plantings.map(planting => (
        <article key={planting.id}>
          <span><FaSeedling /></span>
          <div>
            <strong>{planting.cultura || 'Cultura não informada'}</strong>
            <small>{formatDate(planting.dataPlantio)} → {formatDate(planting.dataColheita || planting.dataColheitaPrevista)}</small>
            <em>{planting.status || 'Histórico preservado'}</em>
          </div>
        </article>
      ))}
    </div>
  );
}

export default function PlotMigrationWizard({
  darkMode = false,
  currentUserName = 'Produtor',
  onBack,
  previewMode = false,
  localReviewMode = false,
  reviewerMode = false,
  onMigrationCompleted,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [selectedPlotId, setSelectedPlotId] = useState('');
  const [drafts, setDrafts] = useState({});
  const [editorPlot, setEditorPlot] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      try {
        let payload;
        if (reviewerMode) {
          payload = await loadInternalPlotMigrationReview();
        } else if (localReviewMode) {
          const response = await fetch(`${LOCAL_REVIEW_DATA_URL}?v=${Date.now()}`, { cache: 'no-store' });
          if (!response.ok) throw new Error('O retrato real da homologação ainda não foi publicado neste localhost.');
          payload = await response.json();
          if (!payload?.somenteLeitura || payload?.producaoConectada || payload?.ambiente !== 'homologacao') {
            throw new Error('O retrato local não passou nas verificações de segurança.');
          }
        } else if (previewMode) {
          payload = CLIENT_PREVIEW_DATA;
        } else {
          payload = await loadOwnedPlotMigrationReview();
        }

        if (cancelled) return;
        const properties = payload?.propriedades || [];
        const loadedDrafts = {};
        properties.forEach(property => (property.talhoes || []).forEach(plot => {
          const key = draftKey(plot);
          loadedDrafts[key] = initialDraft(plot);
        }));
        setData(payload);
        setDrafts(loadedDrafts);
        setSelectedPropertyId(properties[0]?.id || '');
        setSelectedPlotId(properties[0]?.talhoes?.[0]?.id || '');
      } catch (loadError) {
        if (!cancelled) setError(loadError.message || 'Não foi possível abrir a revisão.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [localReviewMode, previewMode, reviewerMode]);

  const properties = data?.propriedades || [];
  const property = properties.find(item => String(item.id) === String(selectedPropertyId)) || properties[0] || null;
  const plots = property?.talhoes || [];
  const plot = plots.find(item => String(item.id) === String(selectedPlotId)) || plots[0] || null;
  const currentDraft = plot ? (drafts[draftKey(plot)] || initialDraft(plot)) : null;

  useEffect(() => {
    if (properties.length > 0 && !properties.some(item => String(item.id) === String(selectedPropertyId))) {
      setSelectedPropertyId(properties[0].id);
    }
  }, [properties, selectedPropertyId]);

  useEffect(() => {
    if (property && !plots.some(item => String(item.id) === String(selectedPlotId))) {
      setSelectedPlotId(plots[0]?.id || '');
    }
  }, [plots, property, selectedPlotId]);

  const isPlotComplete = candidate => {
    const draft = drafts[draftKey(candidate)] || initialDraft(candidate);
    if (!draft.saved) return false;
    if (draft.decision === 'pendente_cliente') return true;
    if (draft.decision === 'agrupar') return Boolean(draft.destinationPhysicalPlotId);
    return Boolean(draft.geometry || candidate.geometria);
  };

  const preparationReviewMode = reviewerMode || localReviewMode;
  const totalQueueItems = preparationReviewMode
    ? plots.length
    : Number(data?.resumo?.total ?? properties.reduce((total, item) => total + (item.talhoes?.length || 0), 0));
  const pendingQueueItems = preparationReviewMode
    ? 0
    : Number(data?.resumo?.pendentes ?? properties.reduce((total, item) => total + (item.talhoes?.length || 0), 0));
  const completedCount = preparationReviewMode ? plots.length : Math.max(0, totalQueueItems - pendingQueueItems);
  const progress = totalQueueItems
    ? Math.round((completedCount / totalQueueItems) * 100)
    : 100;
  const usesLocalDrafts = previewMode;
  const writesEnabled = !preparationReviewMode && (usesLocalDrafts || plotMigrationRuntime.writesEnabled);

  const updateDraft = patch => {
    if (!plot) return;
    const key = draftKey(plot);
    setDrafts(current => ({
      ...current,
      [key]: { ...(current[key] || initialDraft(plot)), ...patch, saved: false, status: 'pendente' },
    }));
    setMessage('');
  };

  const removeEffectuatedPlot = (legacyPlotId, result) => {
    setData(current => {
      if (!current) return current;
      const nextProperties = (current.propriedades || [])
        .map(item => ({
          ...item,
          talhoes: (item.talhoes || []).filter(candidate => (
            String(candidate?.legado?.id) !== String(legacyPlotId)
          )),
        }))
        .filter(item => item.talhoes.length > 0);
      const previousSummary = current.resumo || {};
      const total = Number(previousSummary.total ?? totalQueueItems);
      const pending = Number(result?.itens_pendentes_usuario ?? Math.max(0, pendingQueueItems - 1));
      return {
        ...current,
        propriedades: nextProperties,
        resumo: {
          ...previousSummary,
          total,
          pendentes: pending,
          efetivados: Math.max(0, total - pending),
        },
      };
    });
  };

  const saveCurrentPlot = async () => {
    if (!plot || !currentDraft) return;
    if (preparationReviewMode) {
      setMessage('Esta conferência é somente leitura. A configuração será feita pelo próprio cliente.');
      return;
    }
    if (currentDraft.decision === 'manter_separado') {
      if (currentDraft.plotName.trim().length < 2) {
        setMessage('Informe o nome definitivo deste talhão físico.');
        return;
      }
      if (!(currentDraft.geometry || plot.geometria)) {
        setMessage('Cadastre o perímetro desta área antes de efetivar.');
        return;
      }
    }
    if (currentDraft.decision === 'agrupar' && !currentDraft.destinationPhysicalPlotId) {
      setMessage('Escolha em qual talhão físico este plantio deve entrar.');
      return;
    }

    const confirmed = window.confirm(
      'Confirma a organização deste talhão? Ele sairá da fila de migração. O cadastro antigo e todo o histórico continuarão preservados.'
    );
    if (!confirmed) return;

    setSaving(true);
    setMessage('');
    try {
      let result;
      if (!usesLocalDrafts) {
        result = await effectuateOwnedPlotMigration({
          legacyPlotId: plot.legado.id,
          plotName: currentDraft.plotName,
          decision: currentDraft.decision,
          destinationPhysicalPlotId: currentDraft.decision === 'agrupar'
            ? currentDraft.destinationPhysicalPlotId
            : null,
          geometry: currentDraft.decision === 'manter_separado'
            ? (currentDraft.geometry || plot.geometria)
            : null,
          calculatedArea: currentDraft.calculatedArea,
          observation: currentDraft.justification,
        });
      } else {
        const simulatedPending = Math.max(0, pendingQueueItems - 1);
        result = {
          efetivado: true,
          itens_pendentes_usuario: simulatedPending,
          menu_visivel: simulatedPending > 0,
        };
      }
      removeEffectuatedPlot(plot.legado.id, result);
      setMessage('Talhão efetivado com segurança. Ele foi retirado da fila; o cadastro antigo continua preservado.');
      if (result?.menu_visivel === false) onMigrationCompleted?.();
    } catch (saveError) {
      setMessage(saveError.message || 'Não foi possível efetivar este talhão.');
    } finally {
      setSaving(false);
    }
  };

  const handlePerimeterSave = perimeter => {
    updateDraft({
      geometry: perimeter.geometria,
      calculatedArea: perimeter.areaCalculada,
    });
    setEditorPlot(null);
    setMessage('Perímetro desenhado. Confira o nome e a organização antes de efetivar.');
  };

  if (loading) {
    return <main className={`plot-migration-wizard ${darkMode ? 'is-dark' : ''}`}><div className="plot-migration-state">Carregando seus talhões…</div></main>;
  }

  if (error) {
    return (
      <main className={`plot-migration-wizard ${darkMode ? 'is-dark' : ''}`}>
        <div className="plot-migration-state is-error"><FaExclamationTriangle /><strong>Não foi possível abrir a revisão</strong><span>{error}</span>{onBack && <button type="button" onClick={onBack}>Voltar</button>}</div>
      </main>
    );
  }

  if (!property) {
    return (
      <main className={`plot-migration-wizard ${darkMode ? 'is-dark' : ''}`}>
        <div className="plot-migration-state"><FaCheckCircle /><strong>Nenhuma migração pendente</strong><span>Não há talhões antigos preparados para esta conta.</span>{onBack && <button type="button" onClick={onBack}>Voltar</button>}</div>
      </main>
    );
  }

  return (
    <main className={`plot-migration-wizard ${darkMode ? 'is-dark' : ''}`}>
      <header className="plot-migration-header">
        <div>
          {onBack && <button type="button" className="plot-migration-back" onClick={onBack}><FaArrowLeft /> Voltar</button>}
          <span>{preparationReviewMode ? 'Conferência da migração' : 'Atualização segura dos seus talhões'}</span>
          <h1>{preparationReviewMode ? 'Dados antigos preparados para o cliente' : `Olá, ${currentUserName?.split?.(' ')?.[0] || 'Produtor'}. Vamos organizar seu histórico.`}</h1>
          <p>{preparationReviewMode ? 'Confira se propriedades, talhões e plantios foram preservados. O próprio cliente definirá nomes e perímetros no novo software.' : 'Você confirma onde ficam as áreas físicas; o ClimoraX preserva os plantios antigos dentro delas.'}</p>
        </div>
        <div className="plot-migration-progress-card">
          <strong>{progress}%</strong>
          <span>{preparationReviewMode ? `${plots.length} cadastro(s) preparado(s)` : `${completedCount} de ${totalQueueItems} talhões efetivados`}</span>
          <i><b style={{ width: `${progress}%` }} /></i>
        </div>
      </header>

      <section className="plot-migration-safety">
        <FaShieldAlt />
        <div><strong>Os dados antigos continuam intactos</strong><span>{preparationReviewMode ? 'Esta tela não altera nomes, mapas ou agrupamentos. Ela apenas confere o conteúdo preparado na homologação.' : 'Cada confirmação altera somente a estrutura nova. Fotos, históricos e o acesso ao aplicativo antigo não são excluídos nem modificados.'}</span></div>
        <small><FaLock /> {preparationReviewMode ? 'Somente leitura' : 'Efetivação individual e protegida'}</small>
      </section>

      {previewMode && (
        <section className="plot-migration-preview-note">
          Simulação segura com dados fictícios. Os botões desta tela não gravam na homologação nem na produção.
        </section>
      )}

      {localReviewMode && (
        <section className="plot-migration-preview-note">
          Dados reais da cópia de homologação · conferência somente leitura. Nenhuma escolha feita nesta tela grava ou altera a homologação ou a produção.
        </section>
      )}

      {reviewerMode && (
        <section className="plot-migration-preview-note">
          Módulo interno da dev_alexandre · somente leitura. O cliente fará a configuração de nomes, perímetros e organização dos plantios quando entrar no novo software.
        </section>
      )}

      {properties.length > 1 && (
        <nav className="plot-migration-property-tabs" aria-label="Escolher propriedade">
          {properties.map(item => <button type="button" key={item.id} className={String(item.id) === String(property.id) ? 'is-active' : ''} onClick={() => setSelectedPropertyId(item.id)}>{item.nome}</button>)}
        </nav>
      )}

      <section className="plot-migration-workspace">
        <aside className="plot-migration-sidebar">
          <div><span>Propriedade</span><h2>{property.nome}</h2><p>{property.usuarioNome || property.usuarioEmail ? `Cliente: ${property.usuarioNome || property.usuarioEmail}` : 'Confira um talhão por vez.'}</p></div>
          <ol>
            {plots.map((item, index) => {
              const complete = isPlotComplete(item);
              const active = String(item.id) === String(plot?.id);
              const itemStatus = preparationReviewMode
                ? item.geometria ? 'Preparado · cliente poderá ajustar' : 'Preparado · cliente desenhará o perímetro'
                : complete
                  ? 'Efetivado'
                  : item.geometria ? 'Confirmar e efetivar' : 'Cadastrar perímetro';
              return (
                <li key={item.id}>
                  <button type="button" className={`${active ? 'is-active' : ''} ${complete ? 'is-complete' : ''}`} onClick={() => { setSelectedPlotId(item.id); setMessage(''); }}>
                    <i>{preparationReviewMode || complete ? <FaCheck /> : index + 1}</i>
                    <span><strong>{item.nome}</strong><small>{itemStatus}</small></span>
                    <FaChevronRight />
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>

        {plot && currentDraft && (
          <div className="plot-migration-detail">
            <div className="plot-migration-comparison">
              <article className="is-legacy">
                <span>Como estava antes</span>
                <h2>{plot.legado?.nome || plot.nome}</h2>
                <dl><div><dt>Plantio/cultura</dt><dd>{plot.legado?.cultura || 'Não informado'}</dd></div><div><dt>Área cadastrada</dt><dd>{formatArea(plot.legado?.area || plot.area)}</dd></div></dl>
              </article>
              <FaChevronRight />
              <article className="is-physical">
                <span>Como ficará organizado</span>
                <h2>{currentDraft.decision === 'agrupar'
                  ? plots.find(item => String(item.id) === String(currentDraft.destinationPhysicalPlotId))?.nome || 'Escolha o talhão físico'
                  : currentDraft.plotName || plot.nome}</h2>
                <dl><div><dt>Talhão físico</dt><dd>Permanece na fazenda</dd></div><div><dt>Plantios</dt><dd>{plot.plantios?.length || 0} no histórico</dd></div></dl>
              </article>
            </div>

            <section className="plot-migration-history-panel">
              <header><FaHistory /><div><span>Histórico preservado</span><h3>Plantios que irão acompanhar esta área</h3></div></header>
              <PlantingHistory plantings={plot.plantios} />
            </section>

            {preparationReviewMode ? (
              <section className="plot-migration-preparation-handoff">
                <header><FaShieldAlt /><div><span>Preparação concluída</span><h3>Este cadastro será configurado pelo cliente</h3></div></header>
                <p>Nenhuma definição de localização, nome ou agrupamento será feita por Alexandre ou Roberto nesta tela.</p>
                <div>
                  <span><FaCheck /><strong>Dados preservados</strong><small>Propriedade, cadastro antigo e histórico de plantios já estão disponíveis.</small></span>
                  <span><FaMapMarkedAlt /><strong>Configuração pelo cliente</strong><small>O cliente poderá editar o nome, desenhar ou corrigir o perímetro e organizar os plantios no talhão físico correto.</small></span>
                </div>
              </section>
            ) : (
            <>
            <section className="plot-migration-decision-panel">
              <header><FaLayerGroup /><div><span>Sua confirmação</span><h3>Este cadastro representa qual área física?</h3></div></header>
              <div className="plot-migration-choice-grid">
                <button type="button" className={currentDraft.decision === 'manter_separado' ? 'is-selected' : ''} onClick={() => updateDraft({ decision: 'manter_separado', destinationPhysicalPlotId: '' })}>
                  <FaMapMarkedAlt /><span><strong>É um talhão físico próprio</strong><small>Manter esta área separada e preservar os plantios nela.</small></span>
                </button>
                <button type="button" className={currentDraft.decision === 'agrupar' ? 'is-selected' : ''} onClick={() => updateDraft({ decision: 'agrupar' })}>
                  <FaLayerGroup /><span><strong>É a mesma área de outro cadastro</strong><small>Levar este plantio para um talhão físico já listado.</small></span>
                </button>
              </div>

              {currentDraft.decision === 'agrupar' ? (
                <label className="plot-migration-field">
                  <span>Escolha o talhão físico correto</span>
                  <select value={currentDraft.destinationPhysicalPlotId} onChange={event => updateDraft({ destinationPhysicalPlotId: event.target.value })}>
                    <option value="">Selecione uma área</option>
                    {plots.filter(item => String(item.id) !== String(plot.id)).map(item => <option key={item.id} value={item.id}>{item.nome} · {formatArea(item.area)}</option>)}
                  </select>
                </label>
              ) : (
                <>
                  <label className="plot-migration-field">
                    <span>Nome definitivo do talhão físico</span>
                    <input value={currentDraft.plotName} onChange={event => updateDraft({ plotName: event.target.value })} placeholder="Ex.: Pivô Central" />
                  </label>
                  <div className={`plot-migration-geometry ${currentDraft.geometry || plot.geometria ? 'is-ready' : 'is-pending'}`}>
                    <FaDrawPolygon />
                    <div>
                      <strong>{currentDraft.geometry ? 'Novo perímetro desenhado' : plot.geometria ? 'Perímetro antigo localizado' : 'Perímetro ainda não cadastrado'}</strong>
                      <span>{currentDraft.geometry
                        ? `${formatArea(currentDraft.calculatedArea)} · pronto para ser efetivado.`
                        : plot.geometria
                          ? 'Você pode manter o desenho identificado ou corrigi-lo no mapa.'
                          : 'Desenhe os limites no mapa para identificar este talhão físico.'}</span>
                    </div>
                    <button type="button" onClick={() => setEditorPlot(plot)}>{currentDraft.geometry || plot.geometria ? 'Revisar no mapa' : 'Cadastrar perímetro'}</button>
                  </div>
                </>
              )}

              <label className="plot-migration-field">
                <span>Observação (opcional)</span>
                <textarea value={currentDraft.justification} onChange={event => updateDraft({ justification: event.target.value })} placeholder="Ex.: este cadastro é a safra 2024 do Pivô Central." />
              </label>

              {message && <p className="plot-migration-message" role="status">{message}</p>}

              <div className="plot-migration-actions">
                <span><FaShieldAlt /> Ao confirmar, este item sai da fila. O cadastro antigo permanece preservado.</span>
                <button type="button" disabled={saving || !writesEnabled} onClick={saveCurrentPlot}><FaCheckCircle /> {saving ? 'Efetivando…' : 'Efetivar este talhão'}</button>
              </div>
              {!writesEnabled && <p className="plot-migration-write-lock">A gravação está desligada neste ambiente até a homologação ser autorizada.</p>}
            </section>

            <section className="plot-migration-submit">
              <div><strong>A conclusão é feita talhão por talhão</strong><span>Restam {pendingQueueItems} item(ns) nesta conta. Quando o último for efetivado, esta opção desaparecerá do menu automaticamente.</span></div>
            </section>
            </>
            )}
          </div>
        )}
      </section>

      {editorPlot && (
        <MigrationPerimeterEditor
          property={{ ...property, talhoes: plots.map(item => ({ ...item, geometria: drafts[draftKey(item)]?.geometry || item.geometria })) }}
          plot={editorPlot}
          existingDraft={{ geometria: drafts[draftKey(editorPlot)]?.geometry || editorPlot.geometria }}
          contextLabel="Revisão do seu talhão físico"
          saveLabel="Usar este perímetro"
          onClose={() => setEditorPlot(null)}
          onSave={handlePerimeterSave}
        />
      )}
    </main>
  );
}
